import { useEffect, useRef, useCallback } from 'react';
import { useAppContext } from '@/context/app';
import {INotification} from '@/types/notification';

const RETRY_LAPSE = 5; // seconds
const WS_SERVER = `${import.meta.env.VITE_APP_WS_API_DOMAIN}/notifications`;

// Connection states for better management
const CONNECTION_STATES = {
    CONNECTING: 0,
    OPEN: 1,
    CLOSING: 2,
    CLOSED: 3,
} as const;

const audio = new Audio('/assets/notification_sound.mp3');
audio.volume = 1;

const WsNotificationConnector = () => {
    const { auth, pushNotification, setWsClient } = useAppContext();
    const userId = auth?.user?._id;

    // Refs for stable references
    const wsClientRef = useRef<WebSocket | null>(null);
    const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isConnectingRef = useRef(false);
    const prevUserIdRef = useRef<string | null>(null);

    // Cleanup function
    const cleanup = useCallback((code?: number, reason?: string) => {
        // Clear any pending retry timeouts
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }

        // Close WebSocket connection if it exists
        if (wsClientRef.current) {
            const ws = wsClientRef.current;
            wsClientRef.current = null;

            // Only close if not already closed or closing
            if (ws.readyState !== CONNECTION_STATES.CLOSED &&
                ws.readyState !== CONNECTION_STATES.CLOSING) {
                ws.close(code || 1000, reason || 'cleanup');
            }
        }

        isConnectingRef.current = false;
    }, []);

    // Connection handler
    const connect = useCallback(() => {
        if (!userId || isConnectingRef.current) return;

        isConnectingRef.current = true;

        try {
            // Cleanup existing connection
            cleanup(1000, 'reconnecting');

            const wsc = new WebSocket(`${WS_SERVER}?user=${userId}`);

            wsc.onopen = () => {
                isConnectingRef.current = false;
                setWsClient(wsc);
                wsClientRef.current = wsc;
                prevUserIdRef.current = userId;
                // console.log(`WebSocket connected for user: ${userId}`);
            };

            wsc.onmessage = ({ data }) => {
                try {
                    const notification:INotification = JSON.parse(data);
                    const settings = notification?.meta?.settings || {}


                    if(notification.scope === 'notification'
                        && !settings?.disableSound
                    ) {
                        audio.play().catch(e => console.error(e));
                        //if(!localStorage.getItem('nse') && notification.scope === 'notification' && new Date(notification.timestamp) > new Date()) {
                    }

                    pushNotification(notification);
                } catch (error) {
                    console.error('[wsMessageError]: Failed to parse message:', error);
                }
            };

            wsc.onclose = ({ code, reason }) => {
                isConnectingRef.current = false;
                wsClientRef.current = null;

                reason;// console.log(`WebSocket closed. Code: ${code}, Reason: ${reason}`);

                // Don't reconnect if closed normally or due to auth
                if (code === 1000 || code === 1008) {
                    return;
                }

                // Reconnect for other closure codes
                if (!retryTimeoutRef.current) {
                    retryTimeoutRef.current = setTimeout(() => {
                        retryTimeoutRef.current = null;
                        connect();
                    }, RETRY_LAPSE * 1000);
                }
            };

            wsc.onerror = (error) => {
                isConnectingRef.current = false;
                console.error('[wsError]: WebSocket error:', error);

                // Error will be followed by onclose, so we'll reconnect there
            };

        } catch (err) {
            isConnectingRef.current = false;
            console.error('[wsConnectionError]: Failed to create WebSocket:', err);

            // Retry on connection failure
            if (!retryTimeoutRef.current) {
                retryTimeoutRef.current = setTimeout(() => {
                    retryTimeoutRef.current = null;
                    connect();
                }, RETRY_LAPSE * 1000);
            }
        }
    }, [userId, cleanup, pushNotification, setWsClient]);

    // Main effect for connection management
    useEffect(() => {
        // Only connect if we have a userId and it's different from previous
        if (userId && userId !== prevUserIdRef.current) {
            connect();
        }

        // Cleanup on unmount or when dependencies change
        // return () => {
        //     cleanup(1000, 'component_unmount');
        // };
    }, [userId, connect, cleanup]);

    // Effect to handle auth changes that might require reconnection
    useEffect(() => {
        // If userId becomes null, cleanup the connection
        if (!userId && prevUserIdRef.current) {
            cleanup(1000, 'user_logged_out');
            prevUserIdRef.current = null;
        }
    }, [userId, cleanup]);

    return null;
};

export default WsNotificationConnector;
